import type { CSSProperties, JSX } from 'react'
import type { IPopupComponentProps } from 'blocks/Popup'

import { useMemo, useRef } from 'react'
import { useClearPopup } from 'blocks/Popup/components/PINLock/handlers'
import { PINLockPlayerWrapper } from 'blocks/Popup/PopupComponent.styled'
import { PopupPINLockHeader } from 'blocks/TextBlock/Templates'
import {
  useScrollFocusedIntoViewOnKeyboard,
  useVisualViewportRect,
} from 'utils/hooks'
import { PINCodeInput } from '../components/PINLock/PINCodeInput'

export interface IPINLockPlayerPopup extends Omit<
  IPopupComponentProps,
  'children'
> {
  title?: string
  description?: string
  invalid?: boolean
  disabled?: boolean
  errorMessage?: string
  onChangeInput?: (value: string) => void
  onCompleteInput?: (value: string) => void
  valueAfterInvalid?: string
}

export const PINLockPlayerPopup = ({
  title,
  description,
  invalid,
  disabled,
  errorMessage,
  onCompleteInput,
  onChangeInput,
  valueAfterInvalid,
}: IPINLockPlayerPopup): JSX.Element => {
  const { inputRef } = useClearPopup({ invalid })
  const rootRef = useRef<HTMLDivElement>(null)
  const { rect, isKeyboardLikelyOpen } = useVisualViewportRect()

  const wrapperStyle = useMemo<CSSProperties | undefined>(
    () =>
      isKeyboardLikelyOpen
        ? {
            top: rect.top + rect.height / 2,
            left: rect.left + rect.width / 2,
          }
        : undefined,
    [isKeyboardLikelyOpen, rect.height, rect.left, rect.top, rect.width]
  )

  useScrollFocusedIntoViewOnKeyboard(rootRef, isKeyboardLikelyOpen, rect.height)

  return (
    <PINLockPlayerWrapper ref={rootRef} style={wrapperStyle}>
      <PopupPINLockHeader title={title} description={description} />
      <PINCodeInput
        ref={inputRef}
        invalid={invalid}
        disabled={disabled}
        errorMessage={errorMessage}
        onComplete={onCompleteInput}
        onChange={onChangeInput}
        valueAfterInvalid={valueAfterInvalid}
      />
    </PINLockPlayerWrapper>
  )
}
